/**
 * Single-row carousel for the Findter Filter & Search app's horizontal filter
 * bar.
 *
 * The app replaces the collection grid with its own markup and re-renders the
 * filter bar on every filter change, so this cannot be a custom element the way
 * `facets-horizontal.js` is. Instead a single controller attaches to the bar,
 * a MutationObserver re-attaches it after each re-render, and the page index
 * lives outside the controller so it survives.
 *
 * Behaviour otherwise mirrors `facets-horizontal.js`: the row never wraps, it
 * pages by a CSS transform rather than a scroller (a scrolling ancestor would
 * clip the open filter panels), and the arrows only appear once the row
 * actually overflows.
 */
(() => {
  const script = document.currentScript;
  const previousLabel = script?.dataset.previousLabel || 'Previous';
  const nextLabel = script?.dataset.nextLabel || 'Next';

  /* The app only lays the filter tree out as a row from 768px up. */
  const desktop = window.matchMedia('screen and (min-width: 768px)');

  /* Matches the app's `min-width: 270px` on the panels. A collapsed panel is
     `display: none` and measures 0, so alignment falls back to this. */
  const PANEL_MIN_WIDTH = 270;

  /* A bar narrower than one filter panel has nothing to page through. */
  const MIN_ROW_WIDTH = PANEL_MIN_WIDTH;

  const ROOT_SELECTOR = '.fdt-layout-horizontal .search-filters__filter-tree-wrapper';
  const TRACK_SELECTOR = '.search-filters__filter-tree-content';
  const OPTION_SELECTOR = '.search-filters__filter-option, .fdt-collection-search--option';
  const PANEL_SELECTOR = '.search-filters__filter-option-item-wrapper, .fdt-collection-search__content';

  /* Survives the app tearing the bar down and rebuilding it. */
  let page = 0;

  const CHEVRONS = {
    prev: 'M7 1 3 5l4 4',
    next: 'm3 9 4-4-4-4',
  };

  function createArrow(direction, label) {
    const button = document.createElement('button');

    button.type = 'button';
    button.setAttribute('data-fdt-carousel-arrow', direction);
    button.setAttribute('aria-label', label);
    button.innerHTML = `<svg aria-hidden="true" focusable="false" fill="none" width="12" viewBox="0 0 10 10"><path d="${CHEVRONS[direction]}" stroke="currentColor" stroke-linecap="square"/></svg>`;

    return button;
  }

  class FindterFacetsCarousel {
    /**
     * @returns {FindterFacetsCarousel|null} null when the app's markup is not
     *   the shape this expects, in which case the bar keeps its own wrapping.
     */
    static create(root) {
      const track = root.querySelector(TRACK_SELECTOR);

      /* The track's own parent becomes the clipping viewport. If the app ever
         renders the track as a direct child of the root there is nothing to
         clip with, and the root itself cannot be used — it has to stay visible
         to lay the arrows out beside the viewport. */
      if (!track || track.parentElement === root) return null;

      return new FindterFacetsCarousel(root, track);
    }

    constructor(root, track) {
      this.root = root;
      this.track = track;
      this.viewport = track.parentElement;
      this.maxPage = 0;

      this.prevButton = createArrow('prev', previousLabel);
      this.nextButton = createArrow('next', nextLabel);

      this.root.setAttribute('data-fdt-carousel', '');
      this.viewport.setAttribute('data-fdt-carousel-viewport', '');
      this.track.setAttribute('data-fdt-carousel-track', '');
      this.root.prepend(this.prevButton);
      this.root.append(this.nextButton);

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.prevButton.addEventListener('click', () => this.goTo(page - 1), { signal });
      this.nextButton.addEventListener('click', () => this.goTo(page + 1), { signal });

      /*
       * `goTo` reads geometry in the same tick as it sets the transitioned
       * transform, so those rects are still the previous page's. Re-align once
       * the slide has settled. The synchronous call in `goTo` still covers the
       * cases where no transition runs (initial layout, reduced motion).
       */
      this.track.addEventListener(
        'transitionend',
        (event) => {
          if (event.propertyName === 'transform') this.alignPanels();
        },
        { signal }
      );

      /* Opening a panel is a class toggle on the option, not a DOM insertion,
         so the observer never sees it — re-align off the click instead, once
         the app has expanded the panel and it has a real width. */
      this.root.addEventListener('click', () => setTimeout(() => this.alignPanels(), 0), { signal });

      this.root.addEventListener(
        'focusin',
        (event) => {
          const option = event.target.closest(OPTION_SELECTOR);
          if (!option || this.maxPage === 0) return;

          const viewportWidth = this.viewport.clientWidth;
          if (viewportWidth === 0) return;

          /*
           * Measure against the track rather than the viewport: both shift by
           * the same transform, so their difference is the option's
           * untransformed offset.
           */
          const offset = option.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
          const target = Math.floor(offset / viewportWidth);

          if (target !== page) this.goTo(target);
        },
        { signal }
      );

      desktop.addEventListener('change', () => this.update(), { signal });

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.track);
    }

    /**
     * True while the hooks this controller installed are still in the document.
     * The app rebuilds the bar on every filter change, which silently discards
     * them.
     */
    get isIntact() {
      return (
        this.root.isConnected &&
        this.track.isConnected &&
        this.track.parentElement === this.viewport &&
        this.prevButton.isConnected &&
        this.nextButton.isConnected
      );
    }

    get isRtl() {
      return getComputedStyle(this.root).direction === 'rtl';
    }

    destroy() {
      this.abortController.abort();
      this.resizeObserver.disconnect();

      this.prevButton.remove();
      this.nextButton.remove();
      this.root.removeAttribute('data-fdt-carousel');
      this.root.removeAttribute('data-fdt-overflowing');
      this.viewport.removeAttribute('data-fdt-carousel-viewport');
      this.track.removeAttribute('data-fdt-carousel-track');
      this.track.style.transform = '';
      this.resetPanels();
    }

    /**
     * Recompute whether the row overflows and clamp the current page to the new
     * page count. Called on every resize of either the viewport or the track.
     */
    update() {
      /*
       * Measured on the app's own container, never on the viewport: this
       * stylesheet sizes the viewport, so deciding on its width would make the
       * decision depend on its own outcome and oscillate.
       */
      const available = this.root.parentElement?.clientWidth ?? 0;

      /*
       * Stand down and hand the bar back to the app when the carousel cannot do
       * its job: below 768px, where the app stacks the filters rather than
       * laying them out as a row, and wherever the bar is too narrow to page
       * through. The narrow case is not hypothetical — on the search page the
       * app mounts inside the theme's `product-list` grid and its whole UI
       * collapses into a single product column, where one unpageable row would
       * hide the filters outright instead of tidying them.
       */
      if (!desktop.matches || available < MIN_ROW_WIDTH) {
        this.root.setAttribute('data-fdt-inactive', '');
        this.root.removeAttribute('data-fdt-overflowing');
        this.track.style.transform = '';
        this.resetPanels();
        this.maxPage = 0;
        return;
      }

      this.root.removeAttribute('data-fdt-inactive');

      const viewportWidth = this.viewport.clientWidth;
      const trackWidth = this.track.scrollWidth;

      /* 1px tolerance absorbs sub-pixel layout rounding. */
      const overflowing = trackWidth > viewportWidth + 1;
      this.root.toggleAttribute('data-fdt-overflowing', overflowing);

      this.maxPage = overflowing && viewportWidth > 0 ? Math.ceil(trackWidth / viewportWidth) - 1 : 0;
      this.goTo(page);
    }

    goTo(target) {
      page = Math.max(0, Math.min(target, this.maxPage));

      const viewportWidth = this.viewport.clientWidth;
      const maxOffset = Math.max(0, this.track.scrollWidth - viewportWidth);
      const offset = Math.min(page * viewportWidth, maxOffset);

      this.track.style.transform = `translateX(${this.isRtl ? offset : -offset}px)`;

      this.prevButton.disabled = page === 0;
      this.nextButton.disabled = page >= this.maxPage;

      this.alignPanels();
    }

    /**
     * Hold every filter's panel inside the viewport's clipping box. Without
     * this, panels near the end of a page are cut off horizontally — including
     * the filter straddling the page boundary, whose own edges are already
     * outside the viewport, so anchoring the panel to them is not enough.
     *
     * Positioned in physical pixels on purpose: an explicit `left` overrides
     * the panel's static position in either writing direction, so the clamp
     * needs no RTL branch.
     */
    alignPanels() {
      const viewportRect = this.viewport.getBoundingClientRect();

      this.track.querySelectorAll(OPTION_SELECTOR).forEach((option) => {
        const panel = option.querySelector(PANEL_SELECTOR);
        if (!panel) return;

        /* A collapsed panel is `display: none` and measures 0; fall back to the
           app's own minimum until the click handler re-runs with it open. */
        const panelWidth = panel.offsetWidth || PANEL_MIN_WIDTH;
        const optionRect = option.getBoundingClientRect();

        /* `max` applied last so a panel wider than the viewport stays pinned to
           the start edge rather than overshooting it. */
        const left = Math.max(viewportRect.left, Math.min(optionRect.left, viewportRect.right - panelWidth));

        panel.style.left = `${Math.round(left - optionRect.left)}px`;
        panel.style.right = 'auto';
      });
    }

    /** Hand the panels back to the app's own positioning. */
    resetPanels() {
      this.track.querySelectorAll(PANEL_SELECTOR).forEach((panel) => {
        panel.style.left = '';
        panel.style.right = '';
      });
    }
  }

  let carousel = null;

  /**
   * Attach to the bar, or re-attach after the app has rebuilt it. Deliberately
   * measures nothing while the current controller is still intact — the app
   * mutates this subtree constantly (infinite scroll appends products into it),
   * and the ResizeObserver already covers every geometry change.
   */
  function sync() {
    const root = document.querySelector(ROOT_SELECTOR);

    if (carousel) {
      if (root && carousel.root === root && carousel.isIntact) return;

      carousel.destroy();
      carousel = null;
    }

    if (!root) return;

    carousel = FindterFacetsCarousel.create(root);
    carousel?.update();
  }

  /* The app mounts and re-renders the bar client-side, so there is no single
     ready event to hang this off — watch its container instead. The app appends
     itself into whichever of these two sections the page has, and both are
     server-rendered and therefore already present for this deferred script.

     `sync` runs straight from the observer callback, which is a microtask in the
     same task as the app's render, so the bar is a single row before the
     browser paints it. Deferring it to a later task (a timeout, as this used to)
     let the wrapped two-to-four-row bar paint first and then collapse, shifting
     the whole product grid up — a CLS of 0.35-0.6 on brand and search pages.
     The callback already receives each render's mutations as one batch, and
     `sync` returns without measuring while the controller is intact, so this
     costs one querySelector per batch. The arrows `sync` inserts queue one more
     batch, which finds the controller intact and stops there. */
  const observed =
    document.querySelector('.shopify-section--main-collection, .shopify-section--main-search') ||
    document.documentElement;
  new MutationObserver(sync).observe(observed, { childList: true, subtree: true });

  sync();
})();
